import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  KEEP_EXPIRY_CODE,
  PLAN_CYCLE_DAYS,
  PLAN_PAYMENT_CODES,
  PLAN_PURCHASE_MODES,
  SKIP_PAYMENT_CODE,
  linePaymentLabel,
} from '@app/shared';
import type {
  CardPaymentInput,
  ChargeQuoteDto,
  LinePaymentMethod,
  PlanChangeRequestDto,
  PlanPurchaseMode,
  PlanPurchaseResultDto,
  PlanPurchaseScreenDto,
  PlanRenewalDto,
} from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { StatusChip } from '../../components/StatusChip';
import { ApiError, api } from '../../lib/api';
import { formatCents, formatDate, formatDateTime } from '../../lib/format';
import { useAuth } from '../auth/AuthProvider';
import { CardField, useCardsAvailable } from '../billing/CardField';
import type { CardFieldHandle } from '../billing/CardField';
import { useCustomerContext } from './CustomerLayout';
import { Section } from './parts';

type Kind = 'Renew' | 'Change';
type Problems = Record<string, string>;
const fromServer = (err: unknown): Problems =>
  err instanceof ApiError ? Object.fromEntries(err.details.map((d) => [d.path, d.message])) : {};

const META: Record<Kind, { title: string; code: string; path: string; word: string }> = {
  Renew: { title: 'Plan Renew', code: 'PC519', path: 'plan-renew', word: 'Renew' },
  Change: { title: 'Plan Change', code: 'PC77', path: 'plan-change', word: 'Change' },
};
const READ_ONLY = { input: { readOnly: true } } as const;
const GRID = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
} as const;
const CYCLE_MS = PLAN_CYCLE_DAYS * 86_400_000;

/** The expiry the screen announces, worked out as the server does (planPurchase.ts). */
function announcedExpiry(
  screen: PlanPurchaseScreenDto,
  mode: PlanPurchaseMode,
  keepExpiry: boolean,
  date: string,
): Date | null {
  const old = screen.planExpiresAt ? new Date(screen.planExpiresAt) : null;
  const start =
    mode === 'Instant'
      ? new Date()
      : mode === 'On Expiry'
        ? old
        : date
          ? new Date(`${date}T00:00:00`)
          : null;
  if (!start) return null;
  if (keepExpiry && old && old.getTime() > start.getTime())
    return new Date(old.getTime() + CYCLE_MS);
  return new Date(start.getTime() + CYCLE_MS);
}

/** The amounts of the reference's screen, read-only: the server works them out. */
function Amounts({ quote }: { quote: ChargeQuoteDto }) {
  const field = (label: string, cents: number) => (
    <TextField key={label} label={label} value={formatCents(cents)} slotProps={READ_ONLY} />
  );
  return (
    <Box sx={{ ...GRID, mb: 2 }} aria-label="Amounts">
      {field('Plan Cost', quote.baseCents)}
      {field('Discount Amount', quote.discountCents)}
      {field('Total Tax Amount', quote.taxCents)}
      {field('Processing Fee', quote.processingFeeCents)}
      {field('Total Amount', quote.totalCents)}
    </Box>
  );
}

const REQUEST_COLUMNS = (
  onCancel: (r: PlanChangeRequestDto) => void,
  busy: boolean,
): Column<PlanChangeRequestDto>[] => [
  { key: '#', header: '#', width: 50, render: (_r, i) => i + 1 },
  { key: 'customerId', header: 'Customer Id', render: (r) => r.customerId },
  { key: 'oldPlan', header: 'Old Plan', render: (r) => r.oldPlan },
  { key: 'newPlan', header: 'New Plan', render: (r) => r.newPlan },
  {
    key: 'effectiveDate',
    header: 'Plan Effective Date',
    nowrap: true,
    render: (r) => formatDate(r.effectiveDate),
  },
  { key: 'mode', header: 'Plan Change Mode', render: (r) => r.mode },
  { key: 'invoice', header: 'Invoice No', nowrap: true, render: (r) => r.invoiceNo },
  {
    key: 'status',
    header: 'Status',
    render: (r) => (
      <Box>
        <StatusChip status={r.status} />
        {r.message ? <Typography sx={{ fontSize: 12 }}>{r.message}</Typography> : null}
      </Box>
    ),
  },
  {
    key: 'action',
    header: 'Action',
    render: (r) =>
      r.status === 'Pending' ? (
        <Button
          size="small"
          disabled={busy}
          onClick={() => onCancel(r)}
          aria-label={`Cancel plan request to ${r.newPlan}`}
        >
          Cancel Plan Request
        </Button>
      ) : null,
  },
];

const RENEWAL_COLUMNS: Column<PlanRenewalDto>[] = [
  { key: '#', header: '#', width: 50, render: (_r, i) => i + 1 },
  { key: 'invoiceNo', header: 'Invoice No', nowrap: true, render: (r) => r.invoiceNo },
  { key: 'plan', header: 'Plan', render: (r) => r.plan },
  { key: 'tax', header: 'Tax', align: 'right', render: (r) => formatCents(r.taxCents) },
  { key: 'amount', header: 'Total', align: 'right', render: (r) => formatCents(r.amountCents) },
  { key: 'method', header: 'Payment Method', render: (r) => linePaymentLabel(r.method) },
  {
    key: 'expires',
    header: 'Plan Expiry Date',
    nowrap: true,
    render: (r) => formatDate(r.expiresAt),
  },
  { key: 'at', header: 'Date', nowrap: true, render: (r) => formatDateTime(r.at) },
  { key: 'by', header: 'Posted By', render: (r) => r.by?.loginId ?? '' },
];

/**
 * Plan Renew (PC519) and Plan Change (PC77): the reference's "CSR Screen For Plan Renew"
 * and "CSR Screen For Plan Change" (product inventory, appendix 13.7). One tab per way of
 * paying the user may use; "New Plan ... Request" with the plan, the mode, "Keep the Expiry
 * as Original Date", the amounts, the "Other Charges" with their skip boxes and "Skip
 * Payment (PC99)"; then the "Advance Plan List". The Coupon Code and Receipt Number are
 * this application's.
 */
export function PlanPurchasePage({ kind }: { kind: Kind }) {
  const { customer } = useCustomerContext();
  const { can } = useAuth();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const meta = META[kind];
  const base = `/customers/${customer.id}/${meta.path}`;

  const cardsAvailable = useCardsAvailable();
  const cardRef = useRef<CardFieldHandle>(null);
  const methods = (Object.keys(PLAN_PAYMENT_CODES) as LinePaymentMethod[]).filter(
    (m) => can(PLAN_PAYMENT_CODES[m]!) && (m !== 'card' || cardsAvailable),
  );
  const [method, setMethod] = useState<LinePaymentMethod | ''>(methods[0] ?? '');
  const [planId, setPlanId] = useState('');
  const [mode, setMode] = useState<PlanPurchaseMode>('Instant');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [keepExpiry, setKeepExpiry] = useState<boolean | null>(null);
  const [skipPayment, setSkipPayment] = useState(false);
  const [skipLateFee, setSkipLateFee] = useState(false);
  const [skipRestoreFee, setSkipRestoreFee] = useState(false);
  const [payByCredit, setPayByCredit] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [couponDraft, setCouponDraft] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [problems, setProblems] = useState<Problems>({});

  const screen = useQuery({
    queryKey: ['customers', customer.id, meta.path],
    queryFn: () => api.get<PlanPurchaseScreenDto>(base),
  });
  const data = screen.data;
  const keep = keepExpiry ?? data?.keepExpiryDefault ?? true;
  const ready = Boolean(data && !data.blocked && (kind === 'Renew' || planId));
  const params = {
    planId: kind === 'Change' ? planId : '',
    method: skipPayment ? '' : method,
    couponCode,
    skipPayment: String(skipPayment),
    skipLateFee: String(skipLateFee),
    skipRestoreFee: String(skipRestoreFee),
  };
  const quote = useQuery({
    queryKey: ['customers', customer.id, meta.path, 'quote', params],
    queryFn: () => api.get<ChargeQuoteDto>(`${base}/quote`, params),
    enabled: ready,
    retry: false,
    placeholderData: (prev) => prev,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['customers'] });
  const buy = useMutation({
    mutationFn: (input: unknown) => api.post<PlanPurchaseResultDto>(base, input),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.post<PlanChangeRequestDto>(`${base}/${id}/cancel`),
    onSuccess: refresh,
  });
  // A coupon the server refuses is shown at its field; the charge then shows no coupon.
  const couponProblem = quote.error ? (fromServer(quote.error).couponCode ?? '') : '';

  if (screen.error)
    return (
      <Section title={`${meta.title} (${meta.code})`}>
        <ErrorState error={screen.error} onRetry={() => void screen.refetch()} />
      </Section>
    );
  if (!data)
    return (
      <Section title={`${meta.title} (${meta.code})`}>
        <LoadingState />
      </Section>
    );

  const modeAllowed = (key: PlanPurchaseMode): boolean => {
    const code = PLAN_PURCHASE_MODES.find((m) => m.key === key)!.code;
    if (!can(code)) return false;
    if (data.expired) return key === 'Instant';
    return data.instantAllowed || key === 'On Expiry';
  };
  const chosenMode = modeAllowed(mode)
    ? mode
    : (PLAN_PURCHASE_MODES.find((m) => modeAllowed(m.key))?.key ?? mode);
  const expiresOn = announcedExpiry(data, chosenMode, keep && !data.expired, effectiveDate);
  // "Pay Through Discount Credit": the credit pays first, the way of paying the rest.
  const useCredit = payByCredit && !skipPayment && data.discountCreditCents > 0;
  const creditCents =
    useCredit && quote.data ? Math.min(data.discountCreditCents, quote.data.totalCents) : 0;
  const words = meta.word.toLowerCase();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found: Problems = {};
    if (kind === 'Change' && !planId) found.planId = 'Select Plan';
    if (!skipPayment && !method) found.method = 'Select Payment Method';
    if (chosenMode === 'Custom Date' && !effectiveDate) found.effectiveDate = 'Select the date';
    if (Object.keys(found).length) return setProblems(found);
    let card: CardPaymentInput | undefined;
    if (!skipPayment && method === 'card') {
      try {
        card = await cardRef.current?.collect();
      } catch (err) {
        return setProblems({ card: (err as Error).message });
      }
    }
    try {
      const done = await buy.mutateAsync({
        ...(kind === 'Change' ? { planId } : {}),
        method: skipPayment ? '' : method,
        card,
        receiptNumber,
        couponCode,
        mode: chosenMode,
        effectiveDate: chosenMode === 'Custom Date' ? effectiveDate : '',
        keepExpiry: keep,
        skipPayment,
        skipLateFee,
        skipRestoreFee,
        payByDiscountCredit: useCredit,
      });
      const credit = done.discountCreditCents
        ? ` (discount credit ${formatCents(done.discountCreditCents)})`
        : '';
      notify.success(
        done.status === 'Completed'
          ? `${meta.title} done. The plan expires on ${formatDate(done.expiresAt)}. ${linePaymentLabel(skipPayment ? 'skip' : method)} ${formatCents(done.quote.totalCents)}${credit}, invoice ${done.invoiceNo}.`
          : `${meta.title} paid${credit} (invoice ${done.invoiceNo}); it takes effect on ${formatDate(done.effectiveDate)}.`,
      );
      setPlanId('');
      setReceiptNumber('');
      setCouponDraft('');
      setCouponCode('');
      setSkipPayment(false);
      setSkipLateFee(false);
      setSkipRestoreFee(false);
      setPayByCredit(false);
      setProblems({});
    } catch (err) {
      const fields = fromServer(err);
      if (Object.keys(fields).length) setProblems(fields);
      else notify.error(err);
    }
  };

  return (
    <Section title={`${meta.title} (${meta.code})`}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        CSR Screen For {meta.title}
      </Typography>
      {data.blocked ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {data.blocked}
        </Alert>
      ) : kind === 'Change' && data.plans.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          No other plan is offered for this line.
        </Alert>
      ) : (
        <Box
          component="form"
          noValidate
          aria-label={meta.title}
          onSubmit={(e) => void submit(e)}
          sx={{ mb: 2 }}
        >
          {methods.length ? (
            <Tabs
              value={method || false}
              onChange={(_e, m: LinePaymentMethod) => {
                setMethod(m);
                setProblems({});
              }}
              aria-label="Payment Method"
              sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
            >
              {methods.map((m) => (
                <Tab
                  key={m}
                  value={m}
                  label={`${linePaymentLabel(m)} (${PLAN_PAYMENT_CODES[m]})`}
                  disabled={skipPayment}
                />
              ))}
            </Tabs>
          ) : null}
          {problems.method ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {problems.method}
            </Alert>
          ) : null}
          <Typography variant="subtitle2" component="h4" sx={{ mb: 1.5 }}>
            New {meta.title} Request
          </Typography>
          <Box sx={{ ...GRID, mb: 2 }}>
            <TextField
              label="Wallet Balance"
              value={formatCents(data.walletBalanceCents)}
              slotProps={READ_ONLY}
            />
            <Box>
              <TextField
                label="Discount Credit Balance"
                value={formatCents(data.discountCreditCents)}
                slotProps={READ_ONLY}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useCredit}
                    disabled={data.discountCreditCents <= 0 || skipPayment}
                    onChange={(e) => setPayByCredit(e.target.checked)}
                  />
                }
                label="Pay Through Discount Credit"
              />
            </Box>
            <TextField
              label="Customer Name"
              value={`${customer.firstName} ${customer.lastName}`}
              slotProps={READ_ONLY}
            />
            <TextField label="Phone Number" value={customer.line.mdn ?? ''} slotProps={READ_ONLY} />
            {kind === 'Renew' ? (
              <TextField
                label="Change Plan To"
                value={data.plan?.name ?? ''}
                slotProps={READ_ONLY}
              />
            ) : (
              <TextField
                select
                required
                label="Change Plan To"
                value={planId}
                onChange={(e) => {
                  setPlanId(e.target.value);
                  setProblems((p) => ({ ...p, planId: '' }));
                }}
                error={Boolean(problems.planId)}
                helperText={
                  problems.planId ||
                  (can('PC232') ? (
                    <Link to="/settings/plans/new">Add New Plan (PC232)</Link>
                  ) : undefined)
                }
              >
                {data.plans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}({p.code}) {formatCents(p.priceCents)}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Existing Plan Expiry Date"
              value={data.planExpiresAt ? formatDateTime(data.planExpiresAt) : ''}
              slotProps={READ_ONLY}
            />
          </Box>

          <FormLabel id={`${meta.path}-mode`} sx={{ display: 'block', mb: 0.5 }}>
            {kind === 'Renew' ? 'Plan Renew Mode' : 'Plan Change Mode (PC518)'}
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby={`${meta.path}-mode`}
            value={chosenMode}
            onChange={(e) => setMode(e.target.value as PlanPurchaseMode)}
          >
            {PLAN_PURCHASE_MODES.map((m) => (
              <FormControlLabel
                key={m.key}
                value={m.key}
                control={<Radio />}
                label={`${m.key} (${m.code})`}
                disabled={!modeAllowed(m.key)}
              />
            ))}
          </RadioGroup>
          {chosenMode === 'Custom Date' ? (
            <TextField
              required
              type="date"
              label={`Plan ${meta.word} Date`}
              value={effectiveDate}
              onChange={(e) => {
                setEffectiveDate(e.target.value);
                setProblems((p) => ({ ...p, effectiveDate: '' }));
              }}
              error={Boolean(problems.effectiveDate)}
              helperText={problems.effectiveDate}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth={false}
              sx={{ width: 260, my: 1 }}
            />
          ) : null}
          <Typography sx={{ my: 1 }} role="note">
            {expiresOn
              ? chosenMode === 'Instant'
                ? kind === 'Renew'
                  ? `on doing this action, your plan will reload immediately and expire on ${formatDateTime(expiresOn.toISOString())}`
                  : `Next plan will be expired on ${formatDateTime(expiresOn.toISOString())}`
                : `The ${words} takes effect on ${formatDate((chosenMode === 'On Expiry' ? data.planExpiresAt : effectiveDate) ?? '')}, is paid now, and the plan will then expire on ${formatDate(expiresOn.toISOString())}.`
              : null}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={keep && !data.expired}
                disabled={data.expired || !can(KEEP_EXPIRY_CODE)}
                onChange={(e) => setKeepExpiry(e.target.checked)}
              />
            }
            label={`Keep the Expiry as Original Date (${KEEP_EXPIRY_CODE})`}
          />

          {quote.data ? <Amounts quote={quote.data} /> : null}
          {quote.data && useCredit ? (
            <Typography sx={{ mb: 1.5 }} aria-label="Total Amount split">
              Discount Credit {formatCents(creditCents)} + Pay Amount{' '}
              {formatCents(quote.data.totalCents - creditCents)} = Total Amount{' '}
              {formatCents(quote.data.totalCents)}
            </Typography>
          ) : null}

          {data.lateFeeCents || data.restoreFeeCents ? (
            <Box sx={{ mb: 1.5 }} aria-label="Other Charges">
              <Typography variant="subtitle2" component="h4">
                Other Charges
              </Typography>
              {data.lateFeeCents ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography>Late Fee Charge : {formatCents(data.lateFeeCents)}</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={skipLateFee}
                        onChange={(e) => setSkipLateFee(e.target.checked)}
                      />
                    }
                    label="Skip late Fee"
                  />
                </Box>
              ) : null}
              {data.restoreFeeCents ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography>Restore Fee Charge : {formatCents(data.restoreFeeCents)}</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={skipRestoreFee}
                        onChange={(e) => setSkipRestoreFee(e.target.checked)}
                      />
                    }
                    label="Skip Restore Fee"
                  />
                </Box>
              ) : null}
            </Box>
          ) : null}

          <Box sx={{ ...GRID, mb: 1 }}>
            {method === 'card' && !skipPayment ? (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <CardField ref={cardRef} customerId={customer.id} error={problems.card} />
              </Box>
            ) : null}
            {method && method !== 'wallet' && method !== 'card' && !skipPayment ? (
              <TextField
                label="Receipt Number"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
            ) : null}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Coupon Code"
                value={couponDraft}
                onChange={(e) => setCouponDraft(e.target.value)}
                error={Boolean(couponProblem || problems.couponCode)}
                helperText={couponProblem || problems.couponCode || undefined}
                slotProps={{ htmlInput: { maxLength: 30 } }}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  setCouponCode(couponDraft.trim());
                  setProblems({});
                }}
              >
                Apply Coupon
              </Button>
            </Box>
          </Box>
          {problems.mode ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {problems.mode}
            </Alert>
          ) : null}
          {can(SKIP_PAYMENT_CODE) ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipPayment}
                  onChange={(e) => setSkipPayment(e.target.checked)}
                />
              }
              label={`Skip Payment (${SKIP_PAYMENT_CODE})`}
            />
          ) : null}
          <Box>
            <Button
              type="submit"
              variant="contained"
              disabled={buy.isPending || Boolean(couponProblem)}
            >
              Submit
            </Button>
          </Box>
        </Box>
      )}

      <Typography variant="h3" sx={{ mb: 1 }}>
        Advance Plan List
      </Typography>
      <DataTable
        label="Advance Plan List"
        columns={REQUEST_COLUMNS(
          (r) =>
            cancel.mutate(r.id, {
              onSuccess: () => notify.success('Plan purchase request is cancelled successfully.'),
              onError: (err) => notify.error(err),
            }),
          cancel.isPending,
        )}
        rows={data.requests}
        rowKey={(r) => r.id}
        emptyText={`No plan ${words} has been made for this customer.`}
      />
      {kind === 'Renew' ? (
        <>
          <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>
            Renewals of this line
          </Typography>
          <DataTable
            label="Renewals of this line"
            columns={RENEWAL_COLUMNS}
            rows={data.renewals}
            rowKey={(r) => r.id}
            emptyText="This plan has not been renewed yet."
          />
        </>
      ) : null}
    </Section>
  );
}
